/**
 * The chart the rankings page shows: which storefronts exist, which day is
 * asked for, and how the UI's chart-type id maps to the stored spelling.
 * Pure; no I/O. The route and the page both read from here so the rules
 * live in one place.
 */

/** Every storefront with a complete top-free chart on 2026-09-06 (114, measured
 *  in artifacts/r-260907-nb1p/probe-storefronts.json: minimum 2,586 rows each). */
export const STOREFRONTS = [
  'ae', 'af', 'ao', 'ar', 'at', 'au', 'az', 'be', 'bf', 'bg', 'bh', 'bj', 'bo', 'br', 'by', 'ca', 'cd', 'cg', 'ch', 'ci',
  'cl', 'cm', 'cn', 'co', 'cr', 'cy', 'cz', 'de', 'dk', 'do', 'dz', 'ec', 'ee', 'eg', 'es', 'fi', 'fr', 'ga', 'gb', 'ge',
  'gh', 'gr', 'gt', 'hk', 'hr', 'hu', 'id', 'ie', 'il', 'in', 'iq', 'it', 'jo', 'jp', 'ke', 'kh', 'kr', 'kw', 'kz', 'la',
  'lb', 'lk', 'lt', 'lu', 'lv', 'ly', 'ma', 'ml', 'mm', 'mo', 'mt', 'mx', 'my', 'mz', 'ng', 'ni', 'nl', 'no', 'nz', 'om',
  'pa', 'pe', 'ph', 'pk', 'pl', 'pt', 'py', 'qa', 'ro', 'rs', 'ru', 'rw', 'sa', 'se', 'sg', 'si', 'sk', 'sn', 'sv', 'th',
  'tn', 'tr', 'tw', 'tz', 'ua', 'ug', 'us', 'uy', 'uz', 've', 'vn', 'ye', 'za', 'zw',
] as const;

/** The storefronts whose category pages are prebuilt; the other 99 render on demand. */
export const PREBUILT_STOREFRONTS = ['us', 'gb', 'de', 'fr', 'jp', 'kr', 'cn', 'ca', 'au', 'es', 'it', 'br', 'in', 'nl', 'se'] as const;

/** First day with a daily chart in `app_rankings`. */
export const MIN_CHART_DAY = '2025-10-04';

/** `app_all_rankings_view_new_3d` holds today and the two days before it. */
export const VIEW_WINDOW_DAYS = 3;

/** The Apple category id the overall (all-categories) chart is stored under. */
export const OVERALL_CHART_CATEGORY = '36';

const STORED_TYPE = { top_free: 'top-free', top_paid: 'top-paid', top_grossing: 'top-grossing' } as const;
export type RankingTypeId = keyof typeof STORED_TYPE;
export type StoredRankingType = (typeof STORED_TYPE)[RankingTypeId];

/** The UI id (`top_free`) to the spelling both tables store (`top-free`); null for anything else. */
export function toStoredRankingType(id: unknown): StoredRankingType | null {
  return typeof id === 'string' && Object.hasOwn(STORED_TYPE, id) ? STORED_TYPE[id as RankingTypeId] : null;
}

export function todayUtc(now: Date = new Date()): string {
  return now.toISOString().slice(0, 10);
}

/** Yesterday UTC: the newest day every storefront's chart is complete for. */
export function defaultChartDay(now: Date = new Date()): string {
  const d = new Date(now);
  d.setUTCDate(d.getUTCDate() - 1);
  return todayUtc(d);
}

/**
 * A day the calendar actually has, written as YYYY-MM-DD.
 *
 * The shape test alone is not enough: `2026-02-30` matches it and is not a date. Exported
 * because any route taking a day or a week start needs exactly this rule, and two spellings
 * of it is how they start disagreeing.
 */
export function isCalendarDay(raw: unknown): raw is string {
  if (typeof raw !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(raw)) return false;
  const parsed = new Date(`${raw}T00:00:00Z`);
  return !Number.isNaN(parsed.getTime()) && todayUtc(parsed) === raw;
}

/** A real calendar day as YYYY-MM-DD between MIN_CHART_DAY and today UTC, else null. */
export function parseChartDay(raw: unknown, now: Date = new Date()): string | null {
  if (!isCalendarDay(raw)) return null;
  if (raw < MIN_CHART_DAY || raw > todayUtc(now)) return null;
  return raw;
}

/** True when the view still holds this day: today or the two days before it. */
export function isWithinViewWindow(day: string, now: Date = new Date()): boolean {
  const oldest = new Date(now);
  oldest.setUTCDate(oldest.getUTCDate() - (VIEW_WINDOW_DAYS - 1));
  return day >= todayUtc(oldest) && day <= todayUtc(now);
}

/** The day after `day`, for a half-open [day, next) timestamp filter. */
export function nextDay(day: string): string {
  const d = new Date(`${day}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + 1);
  return todayUtc(d);
}

/** One row of a chart read, as much of it as the ordering rules need. */
export type ChartRow = { ranking_created_at?: string | null; ranking_position?: number | null; app_store_id?: number | null };

/**
 * The rows of the newest day in a multi-day read, in chart order.
 *
 * The view read comes back newest-timestamp-first, and one day's chart is written by more than one
 * scrape (the category pass lands about 50 s after the overall pass), so the raw order puts the last
 * scrape's stragglers above rank 1. Two scrapes can also tie on a position; the id keeps that stable.
 */
export function newestDayInChartOrder<T extends ChartRow>(rows: readonly T[]): T[] {
  const dayOf = (row: T) => (row.ranking_created_at ?? '').slice(0, 10);
  const newestDay = rows.map(dayOf).toSorted().at(-1);
  return rows
    .filter((row) => dayOf(row) === newestDay)
    .toSorted((a, b) => (a.ranking_position ?? 0) - (b.ranking_position ?? 0) || (a.app_store_id ?? 0) - (b.app_store_id ?? 0));
}

/** A chart position as `mv_rank_hourly_latest` first saw it, keyed by when it was written. */
export type SeenRow = { app_store_id: number; ranking_position: number; observed_at: string };
/** One `app_rankings` row: one per app per chart per day, its rank moved in place by later passes. */
export type RankRow = { app_store_id: number; ranking_position: number; created_at: string };

/**
 * The newest day whose chart is (nearly) whole: at least 90% of the fullest day's positions.
 * A day starts with the morning scrape, so a partial today loses to yesterday. "The most positions"
 * alone would pick an older day over a whole one Apple happened to list 98 apps on (us/6007 2026-10-30).
 */
export function fullestDay(rows: readonly SeenRow[]): string | null {
  const positionsByDay = new Map<string, Set<number>>();
  for (const r of rows) {
    const day = r.observed_at.slice(0, 10);
    positionsByDay.set(day, (positionsByDay.get(day) ?? new Set()).add(r.ranking_position));
  }
  const most = Math.max(0, ...[...positionsByDay.values()].map((p) => p.size));
  return [...positionsByDay].filter(([, p]) => p.size >= most * 0.9).map(([day]) => day).toSorted().at(-1) ?? null;
}

/**
 * One day's `app_rankings` rows as a chart: one app per position, #1 first.
 *
 * The collector upserts one row per app per day, so a later pass moves a rank in place and keeps
 * the first created_at, and an app that left the chart keeps its last rank. That stale row then
 * shares a position with the app that holds it now (about 110 to 130 rows for 100 positions).
 * The holder is the row that moved since `firstSeen` (its position when written), else the newer one.
 */
export function chartOrder<T extends RankRow>(rows: readonly T[], firstSeen: ReadonlyMap<number, number>): T[] {
  const moved = (r: T) => (firstSeen.has(r.app_store_id) && firstSeen.get(r.app_store_id) !== r.ranking_position ? 1 : 0);
  const best = rows.toSorted(
    (a, b) => moved(b) - moved(a) || b.created_at.localeCompare(a.created_at) || a.app_store_id - b.app_store_id
  );
  const byPosition = new Map<number, T>();
  for (const r of best) if (!byPosition.has(r.ranking_position)) byPosition.set(r.ranking_position, r);
  return [...byPosition.values()].toSorted((a, b) => a.ranking_position - b.ranking_position);
}

/**
 * The day nearest to `day` that `hasChart` says has a chart, `day` itself first, then both ways one
 * step at a time, never outside [min, max]. A tie goes to the earlier day. Null when no day in range has one.
 */
export async function nearestChartDay(
  day: string,
  hasChart: (day: string) => Promise<boolean>,
  min: string = MIN_CHART_DAY,
  max: string = todayUtc(),
): Promise<string | null> {
  const at = (step: number) => {
    const d = new Date(`${day}T00:00:00Z`);
    d.setUTCDate(d.getUTCDate() + step);
    return todayUtc(d);
  };
  for (let step = 0; at(-step) >= min || at(step) <= max; step++) {
    const sides = (step ? [at(-step), at(step)] : [day]).filter((d) => d >= min && d <= max);
    const found = await Promise.all(sides.map(hasChart));
    const hit = sides.find((_, i) => found[i]);
    if (hit) return hit;
  }
  return null;
}

/**
 * True when the date picker must refuse `day`: outside [MIN_CHART_DAY, today], or missing from
 * `available`, the days `/api/rankings/days` says have this chart. With no list yet, only the range applies.
 */
export function chartDayDisabled(day: string, available: ReadonlySet<string> | undefined, now: Date = new Date()): boolean {
  if (day < MIN_CHART_DAY || day > todayUtc(now)) return true;
  return available !== undefined && !available.has(day);
}

/** How the date control names a chart day: "Sep 29", with the year only outside the current UTC year ("Oct 4, 2025"). */
export function shortChartDay(day: string, now: Date = new Date()): string {
  const sameYear = day.slice(0, 4) === todayUtc(now).slice(0, 4);
  return new Date(`${day}T00:00:00Z`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: sameYear ? undefined : 'numeric', timeZone: 'UTC' });
}

/** When a scrape saved the chart, in UTC: "Sep 29, 11:02 UTC". */
export function chartSavedAt(iso: string, now: Date = new Date()): string {
  const at = new Date(iso);
  const time = at.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: 'UTC' });
  return `${shortChartDay(todayUtc(at), now)}, ${time} UTC`;
}
